/* eslint-disable max-lines */
import { IconNames } from '@blueprintjs/icons';
import { formatNameLastFirst } from '@strata/helper-functions';
import { ActionButtonText, Button } from '@strata/react-buttons';
import { DateInputFormik } from '@strata/react-date-time';
import { FormPageContainer } from '@strata/react-form-header-and-content';
import { FormSection } from '@strata/react-form-section';
import { Grid, PrimaryData, SecondaryData } from '@strata/react-grid';
import { TextInputFormik } from '@strata/react-inputs';
import { MessagingBlock } from '@strata/react-messaging-block';
import { RadioButtonGroupFormik } from '@strata/react-radio-button-group';
import { TreatmentStatusIndicator } from '@strata/react-status-indicators';
import { validationStrings } from '@strata/strata-ui-strings';
import { Form, Formik } from 'formik';
import { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import * as Yup from 'yup';

import { ReferralAndOnboardingStatusIndicator } from 'components/referrals/ReferralAndOnboardingStatusIndicator';
import { AppRoutes } from 'services/routes';
import { usePostSearchTreatmentsQuery } from 'store/api/agoraMonitorApi';
import {
    useGetAllProductServicesQuery,
    useGetAllSuppliersQuery,
    useGetPayerSegmentsQuery,
} from 'store/api/referenceApi';
import { Grids, useGridState } from 'store/grids/gridsSlice';
import {
    clearTreatmentSearchParams,
    setTreatmentSearchParams,
    TreatmentSearchOptions,
} from 'store/treatment/treatmentSlice';

const InitialSortProperties = [{ id: 'patient', desc: false }];

const columns = [
    {
        accessorKey: 'patient',
        header: 'Patient',
        size: 160,
        cell: ({ getValue }) => <PrimaryData data={getValue()} />,
    },
    {
        accessorKey: 'memberId',
        header: 'Member ID',
        size: 120,
    },
    {
        accessorKey: 'product',
        header: 'Product',
        size: 120,
    },
    {
        accessorKey: 'segmentName',
        header: 'Health Plan',
        size: 120,
    },
    {
        accessorKey: 'supplier',
        header: 'Servicing Supplier',
        size: 120,
    },
    {
        accessorKey: 'referralStatus',
        header: 'Referral Status',
        size: 100,
        cell: ({ getValue, row }) =>
            getValue() ? (
                <ReferralAndOnboardingStatusIndicator
                    referralStatus={getValue().trim()}
                    onboardingStatus={row.original.onboardingStatus}
                    enrollmentStatus={row.original.enrollmentStatus}
                    skipFetch
                />
            ) : (
                <SecondaryData />
            ),
    },
    {
        accessorKey: 'treatmentStatus',
        header: 'Treatment Status',
        size: 80,
        cell: ({ getValue }) => <TreatmentStatusIndicator status={getValue()} />,
    },
];

export const TreatmentSearch = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const { treatmentSearchParams } = useSelector((state) => state.treatment);
    const { sort, updateGridSort } = useGridState(Grids.TREATMENTS, InitialSortProperties);

    const { data: segments } = useGetPayerSegmentsQuery();
    const { data: products } = useGetAllProductServicesQuery();
    const { data: allSuppliersData } = useGetAllSuppliersQuery();
    const { supplierMap } = allSuppliersData ?? {};
    const { data: treatments, isFetching: treatmentsFetching } = usePostSearchTreatmentsQuery(
        {
            memberId: treatmentSearchParams?.memberId,
            patientFirstName: treatmentSearchParams?.patientFirstName,
            patientLastName: treatmentSearchParams?.patientLastName,
            patientDob: treatmentSearchParams?.patientDob,
            treatmentId: treatmentSearchParams?.treatmentId,
            referralId: treatmentSearchParams?.referralId,
            authId: treatmentSearchParams?.authId,
        },
        { skip: !treatmentSearchParams },
    );
    const mappedTreatments = useMemo(
        () =>
            treatments?.map((treatment) => ({
                treatmentId: treatment.treatment_id,
                payerId: treatment.payer_id,
                memberId: treatment.member_id,
                patient: formatNameLastFirst(
                    treatment.patient_last_name,
                    treatment.patient_first_name,
                ),
                product: products?.productServiceMap?.[treatment.product_service_oid],
                segmentName:
                    segments?.payerSegmentMap?.[treatment.current_segment_id]?.segment_name,
                supplier: supplierMap?.[treatment.supplier_npi]?.name ?? treatment.supplier_npi,
                referralStatus: treatment.referral_status,
                onboardingStatus: treatment.onboarding_status,
                enrollmentStatus: treatment.enrollment_status,
                treatmentStatus: treatment.treatment_status,
            })),
        [treatments, products, segments, supplierMap],
    );

    return (
        <FormPageContainer>
            <Formik
                initialValues={
                    treatmentSearchParams ?? { searchOption: TreatmentSearchOptions.Member }
                }
                validationSchema={Yup.object()
                    .shape({
                        searchOption: Yup.string().required(
                            validationStrings.requiredField.errorMessage,
                        ),
                        memberId: Yup.string(),
                        patientFirstName: Yup.string(),
                        patientLastName: Yup.string(),
                        patientDob: DateInputFormik.validator(),
                        treatmentId: Yup.string().when('searchOption', {
                            is: TreatmentSearchOptions.TreatmentId,
                            then: (schema) =>
                                schema.required(validationStrings.requiredField.errorMessage),
                        }),
                        referralId: Yup.string().when('searchOption', {
                            is: TreatmentSearchOptions.ReferralId,
                            then: (schema) =>
                                schema.required(validationStrings.requiredField.errorMessage),
                        }),
                        authId: Yup.string().when('searchOption', {
                            is: TreatmentSearchOptions.AuthId,
                            then: (schema) =>
                                schema.required(validationStrings.requiredField.errorMessage),
                        }),
                    })
                    .test(
                        'at-least-one-member-field',
                        validationStrings.requiredField.errorMessage,
                        (values) =>
                            values?.searchOption !== TreatmentSearchOptions.Member ||
                            !!(
                                values.memberId ||
                                values.patientFirstName ||
                                values.patientLastName ||
                                values.patientDob
                            ),
                    )}
                onSubmit={(values) => {
                    dispatch(setTreatmentSearchParams({ treatmentSearchParams: values }));
                }}
                enableReinitialize
                validateOnMount
            >
                {(formik) => (
                    <div
                        className="flex__gap--medium margin__around--medium"
                        style={{ flexDirection: 'column', height: '100%' }}
                    >
                        <FormSection
                            sectionTitle="Treatment Search"
                            content={
                                <Form>
                                    <RadioButtonGroupFormik
                                        name="searchOption"
                                        label="Search by:"
                                        inlineItems
                                        options={Object.values(TreatmentSearchOptions)}
                                        onChange={(value) => {
                                            void formik.setValues({ searchOption: value });
                                        }}
                                    />
                                    <div className="flex__gap--medium">
                                        {(() => {
                                            switch (formik.values.searchOption) {
                                                case TreatmentSearchOptions.Member:
                                                    return (
                                                        <>
                                                            <TextInputFormik
                                                                id="memberId"
                                                                label="Member ID"
                                                            />
                                                            <TextInputFormik
                                                                id="patientFirstName"
                                                                label="Patient First Name"
                                                            />
                                                            <TextInputFormik
                                                                id="patientLastName"
                                                                label="Patient Last Name"
                                                            />
                                                            <DateInputFormik
                                                                id="patientDob"
                                                                label="Patient DOB"
                                                            />
                                                        </>
                                                    );
                                                case TreatmentSearchOptions.TreatmentId:
                                                    return (
                                                        <TextInputFormik
                                                            id="treatmentId"
                                                            label="Treatment ID"
                                                            required
                                                        />
                                                    );
                                                case TreatmentSearchOptions.ReferralId:
                                                    return (
                                                        <TextInputFormik
                                                            id="referralId"
                                                            label="Referral ID"
                                                            required
                                                        />
                                                    );
                                                case TreatmentSearchOptions.AuthId:
                                                    return (
                                                        <TextInputFormik
                                                            id="authId"
                                                            label="Auth ID"
                                                            required
                                                            helperText="Only treatments with referrals that have associated authorizations will be shown."
                                                        />
                                                    );
                                                default:
                                                    return null;
                                            }
                                        })()}
                                        <div
                                            className="flex__gap--medium"
                                            style={{ flexGrow: 1, alignItems: 'center' }}
                                        >
                                            <Button
                                                type="submit"
                                                label="Search"
                                                disabled={
                                                    !(formik.dirty && formik.isValid) ||
                                                    formik.isSubmitting
                                                }
                                                intent={Button.Intent.PRIMARY}
                                            />
                                            <ActionButtonText
                                                style={{ height: 30 }}
                                                label="Clear Search"
                                                onClick={() =>
                                                    dispatch(clearTreatmentSearchParams())
                                                }
                                            />
                                        </div>
                                    </div>
                                </Form>
                            }
                        />
                        <div
                            style={{
                                display: 'flex',
                                flexDirection: 'column',
                                flexGrow: 1,
                                maxHeight: 'calc(100% - 264px)',
                            }}
                        >
                            <Grid
                                data={treatmentSearchParams && mappedTreatments}
                                isLoading={treatmentsFetching}
                                columns={columns}
                                sortProperties={sort}
                                onSort={updateGridSort}
                                noResults={
                                    <div className="sds_no-results">
                                        <MessagingBlock
                                            size={MessagingBlock.Sizes.MEDIUM}
                                            icon={IconNames.SEARCH}
                                            primaryText="No information to show"
                                            secondaryText="Use the search above to find a patient."
                                        />
                                    </div>
                                }
                                onRowClick={(rowData) =>
                                    navigate(
                                        AppRoutes.treatmentDetails(
                                            rowData.payerId,
                                            rowData.treatmentId,
                                        ),
                                        {
                                            state: {
                                                backToDestination: { text: 'Treatment Search' },
                                            },
                                        },
                                    )
                                }
                            />
                        </div>
                    </div>
                )}
            </Formik>
        </FormPageContainer>
    );
};
